import { number, object, string } from "yup";

import {
  PAYMENT_REMINDER_DEFAULT_INTERVAL,
  PAYMENT_REMINDER_DEFAULT_TIMES,
} from "@/constants/payment-reminder.constants";
import {
  ICustomerConfiguration,
  ICustomerConfigurationInput,
} from "@/interfaces/customer-configuration.interface";
import {
  normalizeUnitDeliveryTimeForApi,
  UNIT_DELIVERY_TIME_MAX_LENGTH,
} from "@/constants/unit-delivery-time.constants";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";
import {
  ConsentExpirationTime,
  FreightOverheadType,
  FreightType,
} from "@/types/customer-configuration.types";

const isEmptyValue = (value: unknown) => value === "" || value === null || value === undefined;

const readConfigNumber = (
  config: ICustomerConfiguration | null | undefined,
  field: "paymentReminderInterval" | "paymentReminderTimes"
): number | null | undefined => {
  if (!config) {
    return undefined;
  }

  const directValue = config[field];
  if (directValue != null && directValue !== "") {
    const parsed = typeof directValue === "number" ? directValue : Number(directValue);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  const pascalField =
    field === "paymentReminderInterval" ? "PaymentReminderInterval" : "PaymentReminderTimes";
  const legacyValue = (config as Record<string, unknown>)[pascalField];

  if (legacyValue == null || legacyValue === "") {
    return undefined;
  }

  const parsed = typeof legacyValue === "number" ? legacyValue : Number(legacyValue);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const optionalReminderIntegerField = (messages: CustomerFormMessages) =>
  string()
    .nullable()
    .test("integer", messages.errorPaymentReminderInteger, (value) => {
      if (isEmptyValue(value)) {
        return true;
      }

      return /^\d+$/.test(String(value));
    });

const requiredPositiveInteger = (messages: CustomerFormMessages) =>
  number()
    .transform((value, originalValue) => {
      if (isEmptyValue(originalValue)) {
        return undefined;
      }

      return value;
    })
    .typeError(messages.errorDaysUntilDueInteger)
    .required(messages.errorRequiredDaysUntilDue)
    .min(1, messages.errorDaysUntilDuePositive)
    .integer(messages.errorDaysUntilDueInteger);

const resolvePaymentReminderValue = (
  value: number | string | null | undefined,
  defaultValue: number
): number => {
  if (isEmptyValue(value)) {
    return defaultValue;
  }

  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const toReminderFormValue = (value: number | null | undefined): string => {
  if (value == null) {
    return "";
  }

  return String(value);
};

export const buildInitialData = (
  config: ICustomerConfiguration | null
): ICustomerConfigurationInput => {
  return {
    freightType: config?.freightType || FreightType.NoFreight,
    daysUntilDue: config?.daysUntilDue ? String(config.daysUntilDue) : "",
    paymentReminderInterval: toReminderFormValue(
      readConfigNumber(config, "paymentReminderInterval")
    ),
    paymentReminderTimes: toReminderFormValue(readConfigNumber(config, "paymentReminderTimes")),
    unitDeliveryTime: config?.unitDeliveryTime?.trim() ?? "",
    freightOverhead: config?.freightOverhead != null ? Number(config.freightOverhead) : 0,
    freightOverheadType: (config?.freightOverheadType as FreightOverheadType) || "",
    consentRequest: config?.consentRequest ?? false,
    consentExpiration: config?.consentExpiration || ConsentExpirationTime.TWELVE_MONTHS,
    consentCancel: config?.consentCancel ?? true,
  };
};

export const normalizeCustomerConfigurationFormData = (
  formData: ICustomerConfigurationInput
): ICustomerConfigurationInput => ({
  freightType: formData.freightType,
  consentRequest: formData.consentRequest,
  consentExpiration: formData.consentExpiration,
  consentCancel: formData.consentCancel,
  daysUntilDue: resolvePaymentReminderValue(formData.daysUntilDue, 1),
  unitDeliveryTime: normalizeUnitDeliveryTimeForApi(formData.unitDeliveryTime),
  freightOverhead: Number(formData.freightOverhead ?? 0),
  freightOverheadType: formData.freightOverheadType || FreightOverheadType.Monetary,
  paymentReminderInterval: resolvePaymentReminderValue(
    formData.paymentReminderInterval,
    PAYMENT_REMINDER_DEFAULT_INTERVAL
  ),
  paymentReminderTimes: resolvePaymentReminderValue(
    formData.paymentReminderTimes,
    PAYMENT_REMINDER_DEFAULT_TIMES
  ),
});

export const buildFormSchema = (messages: CustomerFormMessages) =>
  object().shape({
    freightType: string().required(messages.errorRequiredFreightType),
    daysUntilDue: requiredPositiveInteger(messages),
    paymentReminderInterval: optionalReminderIntegerField(messages),
    paymentReminderTimes: optionalReminderIntegerField(messages),
    unitDeliveryTime: string()
      .max(UNIT_DELIVERY_TIME_MAX_LENGTH, messages.errorUnitDeliveryTimeMaxLength)
      .nullable(),
    freightOverhead: number()
      .transform((value, originalValue) => (isEmptyValue(originalValue) ? 0 : value))
      .typeError(messages.errorFreightOverheadInvalid)
      .min(0, messages.errorFreightOverheadInvalid)
      .nullable(),
  });
